import { ArrowLeft, ArrowRight, KeyRound } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { OtpInput } from '@/components/ui/OtpInput';
import { SectionCard } from '@/components/common/SectionCard';
import { journeySignInPath } from '@/lib/auth/post-auth-landing';
import { OTP_UI } from '@/lib/constants/otp';
import { TWO_FACTOR_SETUP_STRINGS as S } from '@/lib/constants/two-factor-strings';

export interface TotpConfirmCardProps {
  code: string;
  onCodeChange: (code: string) => void;
  /** Local format error for the typed code. */
  codeError?: string;
  /** The confirm request's failure, already worded for the user. */
  submitError?: string;
  isSubmitting: boolean;
  /** False until the setup key exists, so there is nothing to confirm yet. */
  canSubmit: boolean;
}

/**
 * Step 2 of authenticator setup (MYN-241): type the first code from the app.
 * Presentational - the page owns the code, validation and the mutation. Must
 * render inside the page's `<form>`, whose submit this button triggers.
 */
export function TotpConfirmCard({
  code,
  onCodeChange,
  codeError,
  submitError,
  isSubmitting,
  canSubmit,
}: TotpConfirmCardProps) {
  return (
    <SectionCard
      title={S.STEP_CODE_TITLE}
      subtitle={S.STEP_CODE_SUBTITLE}
      icon={<KeyRound size={20} />}
    >
      {submitError && (
        <p role="alert" className="text-danger mb-4 text-center text-sm">
          {submitError}
        </p>
      )}
      <div className="flex flex-col items-center py-4">
        <OtpInput
          label={S.CODE_LABEL}
          value={code}
          onChange={onCodeChange}
          length={OTP_UI.CODE_LENGTH}
          error={codeError}
        />
      </div>
      <div className="mt-4 flex items-center justify-end gap-6">
        <Link
          to={journeySignInPath()}
          className="text-navy hover:text-dkgreen inline-flex items-center gap-1.5 font-sans text-sm font-medium"
        >
          <ArrowLeft size={16} aria-hidden="true" />
          {S.BACK}
        </Link>
        <Button
          type="submit"
          variant="navy"
          disabled={isSubmitting || !canSubmit}
          className="inline-flex items-center gap-2 px-8 py-3.5 text-base"
        >
          {isSubmitting ? S.SUBMITTING : S.SUBMIT}
          <ArrowRight size={18} aria-hidden="true" />
        </Button>
      </div>
    </SectionCard>
  );
}
